import Vue from 'vue';
import axios from 'axios';
import { apolloClient } from '@/plugins/apollo';
import {
  RequestFileUploadUrl,
  ConfirmFullTextPdfUpload,
  ExportReferenceFullTextPdf,
  DeleteFullTextPdf,
} from '@/graphql';
import { CreateFetchFullTextJob } from '@/graphql';
import { Reference } from '@/models';
import { FileUploadKind } from '@/enums';

const actions = {
  async upload(context, { reference, file }) {
    const referenceId = reference.id;
    const reviewId = reference.review.id;
    context.commit('setFile', { referenceId, file });
    context.commit('setProgress', { referenceId, progress: 0 });
    let response;
    try {
      const uploadInfo = await context.dispatch('generateUploadUrl', {
        reviewId,
        name: file.name,
      });
      const uploadUrl = uploadInfo.signedUploadUrl;
      const fileUri = uploadInfo.fileUri;
      const cancelToken = axios.CancelToken.source();
      context.commit('setUploadCancelToken', {
        referenceId,
        cancelToken,
      });
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': 'application/pdf' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          context.commit('setProgress', { referenceId, progress });
        },
        cancelToken: cancelToken.token,
      });

      response = await apolloClient.mutate({
        mutation: ConfirmFullTextPdfUpload,
        variables: {
          referenceId,
          fullTextPdfUri: fileUri,
        },
        refetchQueries: [
          'GetReviewForFullTextPage',
          'GetReviewForReviewPage',
          'GetFullTextReviewPage',
          'GetFullTextReviewConflictsPage',
          'GetDataExtractionPage',
          'GetReferencesByReviewIdAndFilters',
          'GetReferencesByFilters',
          'SearchReferences',
          'GetReferencesPendingFullTextReview',
          'GetReferencesAwaitingYouFullTextReview',
          'GetReferencesFullTextReviewed',
          'GetReferencesForFullTextReviewConflictReview',
        ],
        awaitRefetchQueries: true,
      });

      context.commit('setProgress', { referenceId, progress: 100 });
    } finally {
      context.commit('clearFile', referenceId);
    }
    return response;
  },
  async requestAutoFetch(context, reference) {
    const response = await apolloClient.mutate({
      mutation: CreateFetchFullTextJob,
      variables: { referenceId: reference.id },
      refetchQueries: [
        'GetReviewForFullTextPage',
        'GetReviewForReviewPage',
        'GetReferencesByReviewIdAndFilters',
        'GetReferencesByFilters',
        'GetReferencesPendingFullTextReview',
        'GetReferencesAwaitingYouFullTextReview',
        'GetReferencesFullTextReviewed',
        'GetReferencesForFullTextReviewConflictReview',
      ],
    });
    return new Reference(response.data.createFetchFullTextJob);
  },
  async generateUploadUrl(context, { reviewId, name }) {
    const response = await apolloClient.mutate({
      mutation: RequestFileUploadUrl,
      variables: {
        reviewId,
        kind: FileUploadKind.FULL_TEXT_PDF,
        name,
      },
    });
    return response.data.requestFileUploadUrl;
  },
  cancel(context, referenceId) {
    const cancelToken = context.state.cancelTokens[referenceId];
    if (cancelToken) {
      cancelToken.cancel('Upload cancelled by the user.');
    }
  },
  async fetchDownloadUrl(context, { referenceId, forDownload }) {
    const response = await apolloClient.query({
      query: ExportReferenceFullTextPdf,
      variables: {
        referenceId,
      },
      fetchPolicy: 'network-only',
    });
    const downloadInfo = response.data.exportReferenceFullTextPdf;
    return forDownload
      ? downloadInfo.signedDownloadUrl +
          '&response-content-disposition=attachment'
      : downloadInfo.signedDownloadUrl;
  },
  async delete(context, referenceId) {
    const response = await apolloClient.mutate({
      mutation: DeleteFullTextPdf,
      variables: {
        referenceId,
      },
    });
    return new Reference(response.data.deleteFullTextPdf);
  },
};

const mutations = {
  setProgress(state, { referenceId, progress }) {
    Vue.set(state.progress, referenceId, progress);
  },
  setUploadCancelToken(state, { referenceId, cancelToken }) {
    Vue.set(state.cancelTokens, referenceId, cancelToken);
  },
  setFile(state, { referenceId, file }) {
    Vue.set(state.files, referenceId, file);
  },
  clearFile(state, referenceId) {
    Vue.delete(state.files, referenceId);
  },
};

const state = {
  files: {},
  progress: {},
  cancelTokens: {},
};

const getters = {
  file: (state) => (referenceId) => state.files[referenceId],
  progress: (state) => (referenceId) => state.progress[referenceId],
};

export default {
  namespaced: true,
  actions,
  mutations,
  state,
  getters,
};
